import { useEffect, useRef, useState } from "react";
import { Toaster } from "sonner";
import "@/App.css";
import { Header } from "@/components/cc/Header";
import { VideoStage } from "@/components/cc/VideoStage";
import { Timeline } from "@/components/cc/Timeline";
import { NavBar } from "@/components/cc/NavBar";
import { UploadPanel } from "@/components/cc/UploadPanel";
import { GeneratePanel } from "@/components/cc/GeneratePanel";
import { CaptionsPanel } from "@/components/cc/CaptionsPanel";
import { StylePanel } from "@/components/cc/StylePanel";
import { ExportPanel } from "@/components/cc/ExportPanel";
import { PrivacyDialog } from "@/components/cc/PrivacyDialog";
import { InfoDialog } from "@/components/cc/InfoDialog";
import { useTranscriber } from "@/hooks/useTranscriber";
import { checkDevice } from "@/lib/models";
import { DEFAULT_STYLE } from "@/lib/presets";
import { loadFont } from "@/lib/fonts";

export default function App() {
  const [file, setFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [meta, setMeta] = useState({ duration: 0, w: 9, h: 16 });
  const [captions, setCaptions] = useState([]);
  const [style, setStyle] = useState(DEFAULT_STYLE);
  const [tab, setTab] = useState("upload");
  const [aspect, setAspect] = useState("original");
  const [time, setTime] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [device, setDevice] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(() => !localStorage.getItem("cc_privacy_seen"));
  const [fontTick, setFontTick] = useState(0);
  const videoRef = useRef(null);
  const tr = useTranscriber();

  useEffect(() => { checkDevice().then(setDevice); }, []);
  useEffect(() => { loadFont(style.font).then(() => setFontTick((n) => n + 1)); }, [style.font]);

  const seek = (t) => {
    if (videoRef.current) videoRef.current.currentTime = t;
    setTime(t);
  };

  const onVideo = ({ file: f, url, meta: m }) => {
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setFile(f);
    setVideoUrl(url);
    setMeta(m);
    setCaptions([]);
    setTime(0);
    tr.clear();
  };

  const busy = ["extract", "model", "transcribe"].includes(tr.state.stage);
  const panels = {
    upload: (
      <div className="space-y-5">
        <UploadPanel file={file} meta={meta} onVideo={onVideo} disabled={busy} onImport={setCaptions} />
        <GeneratePanel file={file} meta={meta} device={device} tr={tr} hasCaptions={captions.length > 0}
          onCaptions={(c) => { setCaptions(c); setTab("captions"); }} />
      </div>
    ),
    captions: <CaptionsPanel captions={captions} setCaptions={setCaptions} time={time} seek={seek}
      selectedId={selectedId} setSelectedId={setSelectedId} duration={meta.duration} videoRef={videoRef} goUpload={() => setTab("upload")} />,
    style: <StylePanel style={style} setStyle={setStyle} />,
    export: <ExportPanel file={file} captions={captions} setCaptions={setCaptions} style={style} setStyle={setStyle} mobile={device?.mobile} />,
  };

  return (
    <div className="min-h-screen bg-[var(--cc-bg)] pb-20 md:pb-0" data-testid="app-root">
      <Header onInfo={() => setInfoOpen(true)} onPrivacy={() => setPrivacyOpen(true)} device={device} />
      <main className="md:grid md:grid-cols-12 md:h-[calc(100vh-57px)]">
        <section className="max-md:contents md:col-span-7 md:border-r border-[var(--cc-border)] md:overflow-y-auto cc-scroll">
          <div className="sticky top-0 z-20 bg-[var(--cc-bg)] md:static">
            <VideoStage videoRef={videoRef} videoUrl={videoUrl} meta={meta} aspect={aspect} setAspect={setAspect}
              captions={captions} style={style} onTime={setTime} fontTick={fontTick} onUpload={() => setTab("upload")} />
          </div>
          <Timeline duration={meta.duration} captions={captions} setCaptions={setCaptions} time={time} seek={seek}
            selectedId={selectedId} setSelectedId={(id) => { setSelectedId(id); setTab("captions"); }} />
        </section>
        <section className="md:col-span-5 md:flex md:flex-col md:overflow-hidden">
          <NavBar tab={tab} setTab={setTab} count={captions.length} />
          <div className="p-4 md:p-5 md:flex-1 md:overflow-y-auto cc-scroll" data-testid={`panel-${tab}`}>{panels[tab]}</div>
        </section>
      </main>
      <PrivacyDialog open={privacyOpen} onClose={() => { localStorage.setItem("cc_privacy_seen", "1"); setPrivacyOpen(false); }} />
      <InfoDialog open={infoOpen} onOpenChange={setInfoOpen} />
      <Toaster theme="dark" position="top-center" richColors />
    </div>
  );
}
